/**
 * Asset model.
 *
 * @classdesc This model is the client-side representation of the DAM 'assetBean' object, containing all DAM info on an asset.
 * It has a variety of RPC methods for operating on a single asset.  The object is quite complete, and not all fields
 * are used for all assets.
 *
 * @class
 * @extends Netx.Model
 */
import ProgressModel from '@netx/core-app/models/progress';
import {userInputToExpirationTime} from '@netx/core-asset-expiration/utils/userInputToExpirationTime';
import {assetDefaults} from '@netx/core-asset-next/defaults';
import {checksumEnabled} from '@netx/core-asset-next/enablers';
import {canZoomAsset} from '@netx/core-asset-next/enablers/canZoomAsset';
import {AssetVersionEvent} from '@netx/core-asset-version/actions/constants';
import {getAttribute, getAttributeId} from '@netx/core-attribute/singletons';
import {session} from '@netx/core-auth/collections/singleton/session';
import {dispatcher} from '@netx/core-next/instance';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';
import {URL} from '@netx/core-next/url';
import {matrixNames} from '@netx/core-permissions/properties/assetPermissions';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {i18n} from '@netx/core/i18n';
import Base from '@netx/core/lib/netx-model';
import AssetConstituentsCollection from '../collections/assetConstituents';
import {importTasks} from '../properties/asset';
import {types as assetSetTypes} from '../properties/assetSets';
import AssetSetModel from './assetSet';
import AssetVersionModel from './assetVersion';

const logger = getPrefixedLogger('models/asset');

export default class Asset extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	get idAttribute() {
		return 'assetId';
	}

	get titleAttribute() {
		return 'name';
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {array} */
	get xhrExcludeProgress() {
		return ['recordViewed'];
	}

	/**
	 * @property {object}  defaults                        - The default attribute values
	 * @property {number}  defaults.assetId                - ID
	 * @property {string}  defaults.alphsChannel           - Alpha channel
	 * @property {array}   defaults.attributeIds           - Attribute IDs
	 * @property {array}   defaults.attributeNames         - Attribute names
	 * @property {array}   defaults.attributeValues        - Attribute values
	 * @property {number}  defaults.bitDepth               - Bit depth
	 * @property {array}   defaults.categories             - Category IDs
	 * @property {number}  defaults.categoryid             - Primary category ID
	 * @property {array}   defaults.categoryPaths          - Category paths
	 * @property {number}  defaults.checkout               - If checked out, ID of user who checked it out
	 * @property {string}  defaults.checkoutUserLabel      - Name of user that has this asset currently checked out
	 * @property {string}  defaults.colorProfile           - Color profile
	 * @property {array}   defaults.constituentIds         - Array of ids for all of the assets constituents
	 * @property {string}  defaults.constituentType        - Type of constituent the asset has (keyframes, pages, layers)
	 * @property {string}  defaults.creationDate           - Creation date (YYYY-MM-DD hh:mm:ss)
	 * @property {string}  defaults.description            - Description
	 * @property {number}  defaults.dpi                    - DPI
	 * @property {number}  defaults.duration               - Audio/video duration (seconds)
	 * @property {number}  defaults.expirationDate         - Expiration date (Unix timestamp)
	 * @property {string}  defaults.file                   - Filename
	 * @property {array}   defaults.fileFormatFamily       - File format families (types)
	 * @property {number}  defaults.fileheight             - File height (pixels)
	 * @property {number}  defaults.filesize               - Deprecated. See `fileSize2`
	 * @property {number}  defaults.fileSize2              - File size in bytes
	 * @property {number}  defaults.fileType               - ID of file format type
	 * @property {string}  defaults.fileTypeLabel          - File type name (usually the file extension)
	 * @property {number}  defaults.filewidth              - File width (pixels)
	 * @property {boolean} defaults.flvAvailable           - FLV preview is available
	 * @property {string}  defaults.geoLocationLabel       - Geo location label
	 * @property {boolean} defaults.hasChildren            - Deprecated
	 * @property {number}  defaults.hidden                 - Controls visibility of assets in search results if the asset is part of a group of related assets (this is a way to only show one asset from a group of related assets in search results)
	 * @property {string}  defaults.importDate             - Import date (YYYY-MM-DD hh:mm:ss)
	 * @property {string}  defaults.importUserLabel        - Name of user that imported this asset
	 * @property {boolean} defaults.inCurrentCart          - In current cart (selection)
	 * @property {number}  defaults.latitude               - Latitude (gelocation)
	 * @property {number}  defaults.locationid             - Storage location ID
	 * @property {string}  defaults.locationContext        - Description of storage location
	 * @property {number}  defaults.longitude              - Longitude (gelocation)
	 * @property {string}  defaults.moddate                - Modification date (YYYY-MM-DD hh:mm:ss)
	 * @property {number}  defaults.moduserid              - ID of last user to modify the asset
	 * @property {string}  defaults.moduserlabel           - Name of last user to modify the asset
	 * @property {string}  defaults.name                   - Name
	 * @property {string}  defaults.path                   - Path and filename of the asset in the repository
	 * @property {number}  defaults.parentId               - Deprecated
	 * @property {array}   defaults.permissionMatrix       - Assets permission matrix -- see {module:permissions/properties/assetPermissions#matrixNames} for the 6 permission types
	 * @property {number}  defaults.previewHeight          - Preview height (pixels)
	 * @property {string}  defaults.previewUrl             - Relative URL of preview image
	 * @property {number}  defaults.previewWidth           - Preview width (pixels)
	 * @property {boolean} defaults.proofAvailable         - Proofs available for the asset
	 * @property {number}  defaults.rating                 - Rating (range is 1 - 100)
	 * @property {boolean} defaults.readOnly               - Flag indicating whether the asset is read only
	 * @property {boolean} defaults.relatedAvailable       - Related assets are available
	 * @property {number}  defaults.repurposeAvailability  - Repurpose availability flag
	 * @property {boolean} defaults.reviewAvailable        - Reviews available for the asset
	 * @property {number}  defaults.statDeliveries         - Number of times asset has been downloaded (as defined in Statistics Module)
	 * @property {number}  defaults.statPreviews           - Number of times asset has been viewed (as defined in Statistics Module)
	 * @property {number}  defaults.status                 - Indicates availability of asset file
	 * @property {string}  defaults.thumbUrl               - Relative URL of thumbnail image
	 * @property {number}  defaults.timeScale              - Frames per second
	 * @property {array}   defaults.versionIds             - An array of version ids, for any/all versions of the asset
	 * @property {array}   defaults.viewIds                - Array of ids for all of the views belonging to the asset
	 * @property {array}   defaults.viewNames              - Array of names of all of the views belonging to the asset
	 * @property {number}  defaults.visible                - Visibility flag (1 indicates visible). If set to 0, the asset will only be seen by admins, regardless of permissions
	 * @property {boolean} defaults.zoomAvailable          - Zoom image is available
	 *
	 * @property {boolean} defaults.activatingTemplate     - Activating template (adBuilder)
	 * @property {string}  defaults.audioUrl               - If asset is of type audio, this is the url to the audio (client only)
	 * @property {boolean} defaults.deactivatingTemplate   - Deactivating template (adBuilder)
	 * @property {boolean} defaults.templatable						 - Indicates whether asset can be activated as a template
	 * @property {boolean} defaults.template               - adBuilder template
	 * @property {boolean} defaults.templateCustomizable   - adBuilder template customizable
	 * @property {string}  defaults.videoUrl               - Video URL (client only)
	 * @property {string}  defaults.zoomUrl                - Zoom image (not to be mistaken for zoom tiles) (client only)
	 */
	get defaults() {
		return {
			...assetDefaults,

			// TODO: this should be removed as it never set to anything but `''`
			// not going to do that now as overview thinks this is a thing and maybe somewhere in the bowels
			// of the backbone code it may be being set...
			description: '',
			// Client attributes
			activatingTemplate: false,
			deactivatingTemplate: false,
			audioUrl: '',
			videoUrl: '',
			zoomUrl: '',
		};
	}
	/**
	 * @type {array}
	 */
	get clientAttrs() {
		return [
			'activatingTemplate',
			'audioUrl',
			'customThumbnailFile',
			'progress',
			'thumbnailUpdating',
			'videoUrl',
			'zoomUrl',
		];
	}

	/**
	 * The following RPC methods cause the DAM to operate on the asset described in the model.
	 * All manipulation takes place in the DAM; the browser does not directly manipulate image
	 * data, files or the database.
	 * @rpcs
	 */
	get methods() {
		return {
			/**
			 * fetch(): load the asset described by 'assetId' from the DAM
			 * api method: getAssetBean
			 * @method fetch
			 */
			read: ['getAssetBean', 'assetId'],
			/**
			 * @method
			 * @param {Object} opts Backbone options object, passed to the internal persistence methods.
			 */
			update(opts) {
				return ['setAsset', this.self()];
			},
			/**
			 * destroy(): delete this asset from the DAM.
			 * api method: deleteAsset
			 * @method destroy
			 */
			remove: ['deleteAsset', 'assetId', 'deleteWait'],
			/**
			 * This works for both expiring and unexpiring
			 * Unexpireing just means setting 0 on expirationDate
			 * @method
			 */
			expireAsset: ['expireAsset', 'assetId', 'expirationDate'],
			/**
			 * Import a previously uploaded asset to the DAM
			 * @method
			 */
			importAsset: [
				'importAsset2',
				'file',
				'categoryid',
				'locationid',
				'importAttributes',
			],
			/*
			 * reserve: reserve an asset ID from the DAM for an asset to be imported.
			 * api method: reserveAssetIds
			 * NOTE: not tested.  Use Netx.Collections.Assets.reserve() instead, to reserve N asset IDs at a time.
			 * @private
			 * @method
			 */
			reserve: ['reserveAssetIds', 1],
			/**
			 * addCategory: add this asset to a DAM category.
			 * api method: setAssetCategoryLink
			 * @method
			 * @todo We do not get back an error response, just a boolean
			 */
			addCategory(opts) {
				const d = new $.Deferred();
				this.setAssetCategoryLink(_.extend({}, opts, {link: true})).then(
					(response) => {
						response
							? d.resolveWith(this, [response])
							: d.reject(new Error(i18n.t('assetAddToCategoryError')));
					},
					d.reject,
				);
				return d.promise();
			},
			/**
			 * removeCategory: remove this asset from a DAM category.
			 * api method: setAssetCategoryLink
			 * @method
			 * @todo We do not get back an error response, just a boolean
			 */
			removeCategory(opts) {
				const d = new $.Deferred();
				this.setAssetCategoryLink(_.extend({}, opts, {link: false})).then(
					(response) => {
						response
							? d.resolveWith(this, [response])
							: d.reject(new Error(i18n.t('assetRemoveFromCategoryError')));
					},
					d.reject,
				);
				return d.promise();
			},
			/**
			 * setAssetCategoryLink: add/remove this asset to/from a DAM category.
			 * api method: setAssetCategoryLink
			 * @method
			 * @todo determine & describe the default collision strategy here.
			 * @todo explain what happens when you remove the last category from an asset
			 * @todo We do not get back an error response, just a boolean
			 */
			setAssetCategoryLink(opts) {
				return [
					'setAssetCategoryLink',
					this.id,
					this.linkCategory,
					opts.link || false,
				];
			},
			/**
			 * setCategory: place the asset in exactly one (specified) category, removing it from all others.  (Alias of setCategories())
			 * api method: setAssetCategories
			 * @method
			 */
			setCategory(opts) {
				return [
					'setAssetCategories',
					this.id,
					[this.linkCategory],
					'collisionStrategy.rename',
				];
			},
			/**
			 * setCategories: place the asset in exactly the specified list of categories, removing it from all others.
			 * api method: setAssetCategories
			 * @method
			 */
			setCategories: [
				'setAssetCategories',
				'assetId',
				'linkCategories',
				'collisionStrategy.rename',
			],
			/**
			 * repurpose: run a preset repurposing algorithm on this asset.  (When finished, use the Sharing architecture to get the result.)
			 * api method: repurposeAssetsWithPresetProcess
			 * @method
			 */
			repurpose(opts) {
				return [
					'repurposeAssetsWithPresetProcess',
					[this.id],
					'repurposeOtherIds',
					'repurposeId',
					'repurposeDownloadOverride',
				];
			},
			/**
			 * @method
			 */
			manualRepurpose(opts) {
				return [
					'repurposeAssets',
					[this.id],
					this.repurposeOtherIds,
					this.repurposeMethods,
					this.repurposeValues,
				];
			},
			/**
			 * resync: resync this asset in the DAM
			 * DAM-5726
			 * api method: batchResyncAssets
			 * @method resync
			 * @param {object} opts Request options
			 */
			resync(opts) {
				//rpc needs an *array* of ids (single in this case)
				return ['batchResyncAssets', [this.id], 'all'];
			},
			/*
			 * @deprecated
			 * assetInCurrentCart: tests if this asset is in the current cart.
			 * api method: assetsInCurrentCart
			 * @method
			 * @private
			 *
			 * @todo this method has no parser and appears unused, perhaps untested ... deprecate?
			 * miniAssets have an inCurrentCart attribute from the DAM, which we use instead of this method.
			 * We also flip it manually when adding items to the cart (or we should).
			 * This method might actually have some purpose on a miniAsset, but it's here instead ... my bad, I'm sure ...
			 */
			assetInCurrentCart(opts) {
				return ['assetsInCurrentCart', [this.id]];
			},
			/**
			 * getCarts: returns a set of savedCart objects representing the saved carts in the DAM which
			 * contain this asset.
			 * api method: getCartsForAsset
			 * @deprecated
			 * @method
			 */
			_getCarts: ['getCartsForAsset', 'assetId'],
			/**
			 * checkout: 'checks out' the asset, manipulating its state in various ways, and prepares a download of the asset file.
			 * api method: checkoutAsset
			 * @method
			 */
			checkout: ['checkoutAsset', 'assetId'],
			/**
			 * cancelCheckout: checks-in without file
			 * api method: checkinAsset
			 * @method
			 * @param {object} opts Request options
			 */
			cancelCheckout(opts) {
				return ['checkinAsset', this.id, this.get('file'), '', true];
			},
			/**
			 * checkPendingTasks: track the DAM tasks that fire on asset import
			 * @method
			 */
			checkPendingTasks: ['getImportPendingTasks', 'assetId'],
			/**
			 * checkStatus: check the status of various import tasks.
			 */
			checkStatus: ['getMessage', 'messageType', 'assetId', 'objectContext'],

			/** @todo this method does nothing, because it has no parser.  Remove?  The constituents are normally fetched by Netx.asset.current.constituents ... */
			getConstituents: ['getConstituentObjectsForAsset', 'assetId'],
			/**
			 * Gets the process list (repurpose) for this asset
			 */
			getProcessListForUser: ['getProcessListForUser', 'assetId'],
			/**
			 * recordViewed: tell the DAM statistics package that this asset was viewed once.
			 * api method: recordAssetView
			 * @method
			 */
			recordViewed: ['recordAssetView', 'assetId'],
			/**
			 * makeZoomTiles: tell the DAM to begin making the set of image tiles needed by the asset zoom view.
			 * Once begun, progress on the tile generation is reported by getProgressReport().
			 * api method: generateZoomifyTiles
			 * @method
			 */
			makeZoomTiles: ['generateZoomifyTiles', 'assetId'],

			/**
			 * adBuilder template methods:
			 * ingest a 'template' asset (an .indd file with specific script labels configured)
			 * to become the framework of an adBuilder template.
			 * @method
			 */
			activateAdBuilderTemplate: ['activateAdBuilderTemplate', 'id'],
			deactivateAdBuilderTemplate: ['deactivateAdBuilderTemplate', 'id'],
		};
	}
	/** @type {object} */
	get parsers() {
		return {
			/**
			 * @function
			 * @deprecated
			 * @param {boolean} response Response
			 * @param {object} opts Request options
			 */
			assetInCurrentCart(response, opts) {
				if (this.inCurrentCart !== response[0]) {
					this.inCurrentCart = response[0];
					this.set('inCurrentCart', this.inCurrentCart, opts);
					!opts.silent && this.trigger('sync', this, opts);
				}
			},
			/**
			 * @function
			 * @param {string} uuid UUID
			 * @param {object} opts Request options
			 * @todo We get back a UUID because technically we call checkinAsset and pass `true` to
			 * checkin without a file. The checkin is immediate in this case and we should probably get
			 * a call specifically for cancelling checkout so that we get back the updated bean instead
			 * of tracking a UUID that is finished immediately.
			 * Also, the fact we have to pass more than just our asset id to cancel a checkout is messy
			 */
			cancelCheckout(uuid, opts) {
				if (!uuid) return false;

				// Custom events for resyncing
				this.trigger('cancellingCheckOut', this);

				// Poll
				const batchJob = this.trackBatchJob(uuid, undefined, 500);

				// When completed
				batchJob.completed.then(() => {
					this.trigger('cancelledCheckedOut', this);
					this.fetch();
				});

				return batchJob;
			},
			/**
			 * @function
			 * @param {string} uuid Response (we need to get full beans back as we refactor things)
			 * @param {object} opts Request options
			 */
			checkout(uuid, opts) {
				if (!uuid) return false;

				// Custom events for resyncing
				this.trigger('checkingOut', this);

				// Poll
				const batchJob = this.trackBatchJob(uuid, undefined, 500);

				// When completed
				batchJob.completed.then(() => {
					this.trigger('checkedOut', this);
					this.fetch();
				});

				return batchJob;
			},
			/**
			 * @function
			 * @param {number} assetId Asset id
			 * @param {object} opts Request options
			 */
			importAsset(assetId, opts) {
				if (!assetId) {
					// NOTE: there is an outstanding bug with this returning zero, which is never right.
					console.warn('importReservedAsset returned ' + assetId); // DEBUG;
					return;
				}

				this.set({assetId: assetId});
				// Tell category system that we added an asset.
				// TODO: the category system could just as easily respond to an 'asset:imported' event, which would be more generic & meaningful.
				dispatcher.trigger(
					'asset:category:added',
					this.id,
					this.get('categoryid'),
					this,
					'import',
				);

				return assetId;
			},
			/**
			 * @function
			 * @param {boolean} response Response
			 * @param {object} opts Request options
			 */
			setAssetCategoryLink(response, opts) {
				if (response != true) return;

				dispatcher.trigger(
					'asset:category:added',
					this.id,
					this.linkCategory,
					this,
					'setLink',
				);
				// Track index tasks.
				// DAM-21440?: if just linking, no need to track (hopefully)
				opts?.link == null && dispatcher.trigger('asset:tasks:track', this);
			},
			/**
			 * @function
			 * @param {boolean} response Response
			 * @param {object} opts Request options
			 */
			removeCategory(response, opts) {
				const cat = this.linkCategory;
				dispatcher.trigger('asset:category:removed', this.id, cat, this);
				dispatcher.trigger('asset:tasks:track', this);
			},
			/**
			 * @function
			 * @param {array} cats Category IDs
			 * @param {object} opts Request options
			 */
			setCategories(cats, opts) {
				// server should have given us back the list of ids we gave it
				if (cats.join('') != this.linkCategories.join('')) {
					logger.warn(
						'`setCategories` failed to set categories >',
						cats.join(', '),
						this.linkCategories.join(', '),
					);
				}
			},
			/**
			 * @function
			 * @param {array} ids Asset IDs
			 * @param {object} opts Request options
			 */
			reserve(ids, opts) {
				this.set('assetId', ids[0]);
			},
			/**
			 * @function
			 * @param {string} uuid UUID
			 * @param {object} opts Request options
			 */
			resync(uuid, opts) {
				if (!uuid) {
					return false;
				}

				// Custom events for resyncing
				this.trigger('resyncing', this);

				// Poll
				const batchJob = this.trackBatchJob(uuid);

				// When completed
				batchJob.completed.then(() => {
					this.trigger('resynced', this);
				});

				return batchJob;
			},
		};
	}

	/**
	 * Internally, the Asset model uses another model, 'metadata', to store DAM metadata attributes.
	 * (Backbone.Model uses the key 'attributes' in this object to store all other model data.)
	 * We override the constructor to add that object when the model is created.
	 * @override
	 */
	constructor(attr, opts = {}) {
		super(attr, opts);

		/**
		 * Seperate from the asset model's attributes (which describe DAM data) are the following asset model properties
		 * which inform the various RPC methods of this model.
		 *
		 * For remove():
		 * deleteWait determines if deleteAsset() method will block until the DAM is done deleting.
		 * @type {boolean}
		 * @todo No we wont
		 */
		this.deleteWait = opts.deleteWait !== true;

		/**
		 * importAttributes: an array of LabelValue objects to set as asset metadata attributes upon import
		 * @type {Array}
		 */
		this.importAttributes = opts.importAttributes || [];

		/*
		 * cache of our cart state; 'undefined' means we know nothing.
		 *
		 * @todo As of 7.6.0 assetBeans have inCurrentCart as a property
		 * we need to deprecate all usage of the model property inCurrentCart
		 * in favor of the model attribute inCurrentCart
		 */
		this.inCurrentCart = undefined;

		/**
		 * For addCategory, removeCategory, setCategory:
		 * linkCategory is the category ID these calls operate on.
		 * @type {number}
		 */
		this.linkCategory = opts.linkCategory || 0;

		/**
		 * for setAssetCategories:
		 * linkCategories is a list of category Ids to set on the asset.
		 * @type {array}
		 */
		this.linkCategories = opts.linkCategories || [];
		/*
		 * collisionStrategy defines ways setAssetCategories can handle a filename collision.
		 * Filenames must be unique within categories.  A collision occurs when
		 * we try to move an asset into a category where the asset's filename is already in use.
		 * (Multiple uploads of the same asset are the most common example.)  This integer argument to the
		 * setCategoryAssets() API call specifies how to handle that.  This object maps those
		 * integers to meaningful labels, but we don't allow API users to change the default strategy,
		 * which is collisionStrategy.rename .
		 * @private
		 */
		this.collisionStrategy = _.extend(
			{
				fail: 0, // block the category change; leave the asset in its current category.
				rename: 1, // rename the file we're recategorizing
			},
			opts.collisionStrategy,
		);

		//
		// for getMessage, a nearly-but-not-quite-deprecated API call which we use to track reimports:
		// messageType: 4 = messages about asset reimport.  Other values unknown.
		// objectContext: always null for us.
		// @private
		//
		this.messageType = opts.messageType != null ? opts.messageType : 4;
		/** @type {string} */
		this.objectContext = opts.objectContext || '';

		/**
		 * for repurpose():
		 * repurposeId is the ID of the preset repurposing process to apply with 'repurpose' method. (Netx.asset.processes contains the complete set of those processes, and is indexed by ID.)
		 * @type {number}
		 */
		this.repurposeId = opts.repurposeId || 0;

		/**
		 * repurposeOtherIds: meaning varies by process.
		 * @type {array}
		 */
		this.repurposeOtherIds = opts.repurposeOtherIds || [];
		this.repurposeMethods = opts.repurposeMethods || [];
		this.repurposeValues = opts.repurposeValues || [];

		// according to FlexService.java, this field is 100% ignored.
		this.repurposeDownloadOverride = opts.repurposeDownloadOverride || '';

		// Keep a model of the current version
		this.version = new AssetVersionModel();
		this.addSubModel(this.version);

		this.setMediaUrls();
		this.setChecksum();

		/**
		 * @memberof module:asset/models/asset
		 * @alias progress
		 * @type {module:app/models/progress}
		 */
		this.progress = new ProgressModel();
		this.addSubModel(this.progress);

		/**
		 * @memberof module:asset/models/asset
		 * @alias constituents
		 * @type {module:asset/collections/assetConstituents}
		 */
		this.constituents = new AssetConstituentsCollection([]);
		this.addSubCollection(this.constituents);

		// Hey, listen!
		this
			// Make necessary updates when the id changes
			.listenTo(this, 'change:assetId', (model, assetId, options) => {
				// Update this.version when the asset changes id - sync doesn't work in most of our cases as the data is being
				// set by the cursor change in the controller with data from an asset collection
				// TODO: the asset mimics a sync now when we put it on the cursor - see if this is all still necessary
				// and if there may be a better place to put it
				this.version.clear();
				if (this.id) {
					const versions = this.get('versionIds');
					if (versions && versions.length > 1) {
						this.version.set('versionId', versions[0]);
						this.version.fetch();
					} else if (versions) {
						// Fake it 'til they make it
						this.version.set({
							creationDate: this.get('creationdate'),
							fileSize: this.get('fileSize2'),
							fileTypeId: this.get('filetype'),
							fileTypeLabel: this.get('filetypelabel'),
							assetId: this.get('imageid'),
							userId: this.get('moduserid'),
							userLabel: this.get('moduserlabel'),
						});
						// Mimic a sync
						this.version.trigger('sync', this.version);
					}
				}
				// Custom thumbnail progress listeners
				const oldAssetId = this._previousAttributes.assetId;
				this.setupCustomThumbnailListeners(oldAssetId);
				this.setupAssetTaskListeners(oldAssetId);
				this.setMediaUrls();
				this.setChecksum();
			})
			////////////////////////////
			// ADBUILDER
			////////////////////////////
			.listenTo(
				dispatcher,
				'adBuilder:activate:asset:error',
				(error, asset) => {
					asset &&
						asset.id === this.id &&
						this.set({
							activatingTemplate: false,
							template: false,
							templateCustomizable: false,
						});
				},
			)
			.listenTo(dispatcher, 'adBuilder:activate:asset:start', (asset) => {
				asset &&
					asset.id === this.id &&
					this.set({
						activatingTemplate: true,
						deactivatingTemplate: false,
					});
			})
			.listenTo(dispatcher, 'adBuilder:activate:asset:succeeded', (asset) => {
				asset &&
					asset.id === this.id &&
					this.set({
						activatingTemplate: false,
						template: true,
					});
			})
			.listenTo(
				dispatcher,
				'adBuilder:deactivate:asset:error',
				(error, asset) => {
					asset &&
						asset.id === this.id &&
						this.set({
							deactivatingTemplate: false,
							template: true,
						});
				},
			)
			.listenTo(dispatcher, 'adBuilder:deactivate:asset:start', (asset) => {
				asset &&
					asset.id === this.id &&
					this.set({
						activatingTemplate: false,
						deactivatingTemplate: true,
					});
			})
			.listenTo(dispatcher, 'adBuilder:deactivate:asset:succeeded', (asset) => {
				asset &&
					asset.id === this.id &&
					this.set({
						deactivatingTemplate: false,
						template: false,
						templateCustomizable: false,
					});
			})
			////////////////////////////
			// !ADBUILDER
			////////////////////////////

			////////////////////////////
			// ATTRIBUTES (METADATA)
			////////////////////////////
			.listenTo(
				dispatcher,
				'asset:submit:save asset:info:edit:success asset:attributes:submit:success',
				(m) => {
					if (m.cid !== this.cid && m.id === this.id) {
						this.set(m.attributes);
					}
				},
			)
			////////////////////////////
			// !ATTRIBUTES (METADATA)
			////////////////////////////

			////////////////////////////////
			// CART STATUS
			////////////////////////////////
			// Tracking whether we're in the current cart:
			// If we are added to the cart, we know we're in it
			.listenTo(dispatcher, 'cart:added', (ids) => {
				if (ids.indexOf(this.id) < 0) return;

				if (this.has('inCurrentCart')) {
					this.set('inCurrentCart', true);
					return;
				}

				this.inCurrentCart = true;
				this.trigger('change');
			})
			// If we are removed, we know we're not in it
			.listenTo(dispatcher, 'cart:removed', (ids) => {
				if (ids.indexOf(this.id) < 0) return;

				if (this.has('inCurrentCart')) {
					this.set('inCurrentCart', false);
					return;
				}

				this.inCurrentCart = false;
				this.trigger('change');
			})
			// If the cart is cleared, we know we're not in it.
			.listenTo(dispatcher, 'cart:cleared', () => {
				if (this.has('inCurrentCart')) {
					this.set('inCurrentCart', false);
					return;
				}

				this.inCurrentCart = false;
				this.trigger('change');
			})
			////////////////////////////////
			// !CART STATUS
			////////////////////////////////

			// When a checkout & checkin happens, update our status & checked-out-ness
			.listenTo(
				dispatcher,
				'asset:checkout:success asset:checkout:cancel:success',
				(asset) => {
					// we are us!
					// we are not a duplicate model for the same DAM asset
					if (asset === this || asset.id !== this.id) return;

					this.set({
						status: asset.get('status'),
						checkout: asset.get('checkout'),
					});
				},
			)
			// Batch jobs
			.listenTo(this, 'progress', (model, batchJob, progress) => {
				const progressMessage = (batchJob && batchJob.get('details')) || '';
				this.progress.update(progress, progressMessage);
			})
			.listenTo(this, 'complete', (model, batchJob) => {
				this.progress.complete();
			})
			// When asset sybcs
			.listenTo(this, 'sync', (model) => {
				if (model !== this) return;
				this.progress.reset();
			})
			// When a clip is created/updated
			.listenTo(
				dispatcher,
				'asset:constituent:clip:edit:success',
				(asset, clip, isNew) => {
					if (asset.id !== this.id) return;

					this.constituents.clips.add(clip);
				},
			);

		// Custom thumbnail progress listeners
		this.setupCustomThumbnailListeners();
		this.setupAssetTaskListeners();
		this.setupAssetCheckinListeners();
	}

	/**
	 * The parse() method converts the contents of the AssetBean's attributeNames & attributeValues arrays
	 * into the internal metadata model.
	 * @override
	 * @param {object} resp Asset bean
	 * @param {object} opts Request options
	 * @return {object} the unmodified attributes.  (The internal attribute* arrays will still be updated from this data.)
	 */
	parse(resp, opts) {
		// Set media urls
		return _.extend(_.omit(resp, 'validationData', 'id'), {
			audioUrl: this.hasAudio(resp) || '',
			videoUrl: this.hasVideo(resp) || '',
			zoomUrl: this.hasZoom(resp) || '',
		});
	}

	/**
	 * the self() method translates the metadata object back into arrays,
	 * being careful to preserve the same order as the attributeNames array.
	 * @override
	 * @return {object}
	 */
	self() {
		return _.omit.apply(
			_,
			[
				this.attributes,
				'attributeValues',
				'attributeNames',
				'attributeIds',
			].concat(this.clientAttrs),
		);
	}

	/** @method */
	validate(attrs) {
		if (!_.isNumber(attrs.assetId)) {
			return 'Must pass a number';
		}
	}

	/**
	 * @method
	 * @return {boolean} Can zoom
	 */
	canZoom() {
		return canZoomAsset(this.attributes);
	}

	/**
	 * @method
	 * @param {string} dateStr Date (yyyy-mm-dd)
	 */
	expire(dateStr) {
		try {
			const expirationTime = userInputToExpirationTime(dateStr);
			return this.set('expirationDate', expirationTime).expireAsset();
		} catch (ex) {
			return new $.Deferred().reject(ex);
		}
	}

	/**
	 * @method
	 * @param {object} opts Request options
	 */
	fetchConstituents(opts) {
		var constituents = this.constituents;

		if (constituents.assetId === this.id) {
			return new $.Deferred().resolveWith(constituents);
		}

		constituents.assetId = this.id;
		return constituents.fetch(
			_.extend(
				{
					duplicateQueryPolicy: 'abort',
					reset: true,
				},
				opts,
			),
		);
	}

	garbageCollect() {
		super.garbageCollect();
		delete this.progress;
		delete this.version;
		delete this.constituents;
	}

	/**
	 * getAttribute() and setAttribute() are the preferred interface to the DAM's internal representation of asset attributes.
	 * @method
	 * @param {number} templateId Template id
	 * @param {string} name Template name
	 * @param {object} opts Options
	 * @return {mixed}
	 */
	getAttribute(templateId, name, opts = {}) {
		const attrModel = getAttribute(templateId, name);
		let value;

		if (attrModel) {
			value =
				attrModel.get('system') &&
				attrModel.id !== -119 && // Smart labels
				attrModel.id !== -120 // dimensions
					? this.getSystemAttribute(attrModel.id)
					: this.get('attributeValues')[
							_.indexOf(this.get('attributeIds'), attrModel.id)
					  ];

			value = opts.format
				? attrModel.format(value, opts)
				: attrModel.parseInValue(value);
		}

		return value != null ? value : '';
	}

	/**
	 * Get attribute template id by name
	 * @param {string} name Attribute name
	 * @return {number}
	 */
	getAttributeId(name) {
		return getAttributeId(name);
	}

	/**
	 * @method
	 * @param {number} templateId Template id
	 * @param {object} opts Options
	 * @return {mixed}
	 * @deprecated see `this.getAttribute`
	 */
	getAttributeValue(templateId, opts) {
		return this.getAttribute(templateId, null, opts);
	}

	/**
	 * @method
	 * @param {number} templateId Attribute template id
	 * @return {mixed}
	 */
	getSystemAttribute(templateId, name) {
		const attrModel = getAttribute(templateId, name);
		let value;
		if (attrModel) {
			var attrName = attrModel.get('name');
			// Normalize
			switch (attrModel.get('name')) {
				case 'creationDate':
					attrName = 'creationdate';
					break;
				case 'expireDate':
					attrName = 'expirationDate';
					break;
				case 'fileSize':
					attrName = 'fileSize2';
					break;
				case 'fileType':
					attrName = 'filetypelabel';
					break;
				case 'folderPath':
					attrName = 'categoryPaths';
					break;
				case 'height':
					attrName = 'fileheight';
					break;
				case 'modDate':
					attrName = 'moddate';
					break;
				case 'width':
					attrName = 'filewidth';
					break;
			}
			value = this.get(attrName);
			if (_.isArray(value)) {
				value.join(', ');
			}
		}

		return value != null ? value : '';
	}

	/**
	 * @method
	 * @return {string} Token string if it exists
	 */
	getTokenString() {
		const token = session.getToken();
		return token ? '?token=' + token : '';
	}

	/**
	 * @method
	 * @return {array} Waveform JSON
	 */
	getWaveformJSON() {
		return $.getJSON(
			`/file/asset/${this.id}/view/previewWaveformJSON${this.getTokenString()}`,
		).then((json) => {
			return json.data;
		});
	}

	/**
	 * Does asset have audio, get the audio url
	 * @method
	 * @param {object} attrs Attributes to use (rather than `this.get`)
	 * @return {boolean}
	 */
	hasAudio(attrs = {}) {
		const id = attrs.assetId || this.id;
		if (
			_.indexOf(
				attrs.fileFormatFamily || this.get('fileFormatFamily'),
				'audio',
			) < 0
		) {
			return false;
		}

		let url = false;
		const tokenStr = this.getTokenString();
		// Check in view names
		if (
			_.indexOf(attrs.viewNames || this.get('viewNames'), 'previewMp3') > -1
		) {
			url = [
				'/file/asset/',
				id,
				'/view/previewMp3/a_',
				id,
				'.mp3',
				tokenStr,
			].join('');
		}
		// Check in file type label
		else if ((attrs.filetypelabel || this.get('filetypelabel')) === 'mp3') {
			url = ['/file/asset/', id, '/original/a_', id, '.mp3', tokenStr].join('');
		}

		return url;
	}

	/**
	 * @method
	 * @param {string} format Format
	 * @return {boolean} Has format family
	 */
	hasFormatFamily(format) {
		return _.contains(this.get('fileFormatFamily'), format);
	}

	/**
	 * Does asset have any multimedia (audio/video)
	 * @method
	 * @return {boolean|string} Audio url or false
	 */
	hasMultimedia() {
		return this.hasAudio() || this.hasVideo();
	}

	/**
	 * Does asset have video, get the video url
	 * @method
	 * @param {object} attrs Attributes to use (rather than `this.get`)
	 * @return {boolean|string} Video url or false
	 */
	hasVideo(attrs = {}) {
		const id = attrs.assetId || this.id;
		const tokenStr = this.getTokenString();

		if (
			_.indexOf(
				attrs.fileFormatFamily || this.get('fileFormatFamily'),
				'video',
			) < 0
		) {
			return false;
		}

		let url = false;
		if (
			_.indexOf(attrs.viewNames || this.get('viewNames'), 'previewH264') !== -1
		) {
			url = [
				'/file/asset/',
				id,
				'/view/previewH264/v_',
				id,
				'.mp4',
				tokenStr,
			].join('');
		}
		// Check in file type label
		else if ((attrs.filetypelabel || this.get('filetypelabel')) === 'mp4') {
			url = ['/file/asset/', id, '/original/v_', id, '.mp4', tokenStr].join('');
		}

		return url;
	}

	/**
	 * Does asset have a zoom view (Longest side is 1000), get the zoom url
	 * @method
	 * @param {object} attrs Attributes to use (rather than `this.get`)
	 * @return {boolean|string} Zoom url or false
	 */
	hasZoom(attrs = {}) {
		const id = attrs.assetId || this.id;
		const filetypelabel = attrs.filetypelabel || this.get('filetypelabel');
		return `/file/asset/${id}/zoom/${id}.${filetypelabel}`;
	}

	/**
	 * Only for use in updating attribute values locally (like after a change from elsewhere)
	 * Once we get websockets this will be deprecated
	 * @method
	 * @param {number|string} templateId Attribute template id
	 * @param {string} val Value of the attribute to set
	 */
	setAttribute(templateId, val, opts) {
		const id = _.isString(templateId) ? null : templateId;
		const name = _.isString(templateId) ? templateId : null;
		const attrModel = getAttribute(id, name);

		if (!attrModel) return this;

		const idx = _.indexOf(this.get('attributeIds'), attrModel.id);

		if (attrModel.isCSV || attrModel.isTrueMultivalue()) {
			val = _.arrayToCsv(val);
		}

		if (idx > -1) {
			const attributeValues = _.clone(this.get('attributeValues'));
			attributeValues[idx] = val;
			return this.set(
				{
					attributeValues: attributeValues,
				},
				opts,
			);
		}

		return this.set(
			{
				attributeIds: this.get('attributeIds').concat([attrModel.id]),
				attributeNames: this.get('attributeNames').concat([attrModel.name]),
				attributePermissions: this.get('attributePermissions').concat(['r']),
				attributeValues: this.get('attributeValues').concat([val]),
			},
			opts,
		);
	}

	/**
	 * @method
	 */
	setupAssetCheckinListeners() {
		this.stopListening(dispatcher, `${AssetVersionEvent.UPLOAD}:progress`)
			.stopListening(dispatcher, `${AssetVersionEvent.UPLOAD}:complete`)
			.stopListening(dispatcher, `${AssetVersionEvent.UPLOAD}:error`)
			.listenTo(
				dispatcher,
				`${AssetVersionEvent.UPLOAD}:progress`,
				(progress, assetId, batchJob) => {
					if (assetId !== this.id) return;

					this.trigger('checkingIn', this);
					this.progress.update(progress, batchJob.get('details'));
				},
			)
			.listenTo(
				dispatcher,
				`${AssetVersionEvent.UPLOAD}:succeeded`,
				(assetId, batchJob) => {
					if (assetId !== this.id) return;

					this.trigger('checkedIn', this);
					this.progress.complete();
					// Delay slightly so the progress removal does not seem so abrupt
					_.delay(_.bind(this.fetch, this), 1000);
				},
			)
			.listenTo(
				dispatcher,
				`${AssetVersionEvent.UPLOAD}:error`,
				(error, assetId) => {
					this.trigger('checkedIn', this);
					this.progress.complete();
				},
			)
			.stopListening(dispatcher, 'asset:versions:reactivate:progress')
			.stopListening(dispatcher, 'asset:versions:reactivate:succeeded')
			.stopListening(dispatcher, 'asset:versions:reactivate:error')
			.listenTo(
				dispatcher,
				'asset:versions:reactivate:progress',
				(assetId, progress, progressMessage) => {
					if (assetId !== this.id) return;
					this.progress.update(progress, progressMessage);
				},
			)
			.listenTo(
				dispatcher,
				'asset:versions:reactivate:succeeded',
				(newVersion) => {
					if (newVersion.get('assetId') !== this.id) return;
					this.progress.complete();
				},
			)
			.listenTo(
				dispatcher,
				'asset:versions:reactivate:error',
				(error, newVersion) => {
					if (newVersion.get('assetId') !== this.id) return;
					this.progress.complete();
				},
			);
	}

	/**
	 * Setup asset task listeners (cleans up any old listeners as well)
	 * @method
	 * @param {number} oldAssetId Old asset id
	 */
	setupAssetTaskListeners(oldAssetId) {
		if (oldAssetId) {
			// asset:task:complete:<assetId>
			this.stopListening(dispatcher, 'asset:task:complete:' + oldAssetId);
			this.stopListening(dispatcher, 'asset:task:' + oldAssetId + ':status');
		}

		if (!this.id) return;

		// Task completion
		this.listenTo(dispatcher, 'asset:task:complete:' + this.id, (task) => {
			// HACKETY-HACK-SMACK-HACK for EIGHT-549:
			const tempIconPattern = /\/icons\/.*\.png$/;
			let previewNow = this.get('previewUrl');

			// If we currently have no previewUrl, or a temporary icon preview ...
			if (!previewNow || previewNow.match(tempIconPattern)) {
				// ... then poll until the DAM generates a proper preview, or we run out of patience.
				const patienceExhausted = 4; // only try this many times.
				let timesPolled = 0;
				this.pollUntil(
					() => {
						previewNow = this.get('previewUrl'); // possibly updated?
						timesPolled++;
						return (
							(previewNow && !previewNow.match(tempIconPattern)) ||
							timesPolled >= patienceExhausted
						);
					},
					undefined,
					undefined,
					2000,
				);
			}
			this.progress.complete();
			this.trigger('taskComplete', this, task);
		})
			// Task status
			.listenTo(dispatcher, 'asset:task:' + this.id + ':status', (task) => {
				// Shows our import tasks percentage as the second half of our total import progress;
				// therefore, we divide by 2 and add 50.
				const realProgress = task.get('percentComplete') / 2 + 50;
				const tasks = task.get('tasks') || [];
				this.progress.update(
					realProgress,
					importTasks[tasks[0]] || i18n.t('i18n.processing'),
				);
				this.trigger('taskStatus', this, task);
			});
	}

	/**
	 * Cleans up old listeners (if any) and adds new listeners for all your custom thumbnail needs
	 * @method
	 * @param {number} oldAssetId Old asset id
	 */
	setupCustomThumbnailListeners(oldAssetId) {
		// NOTE: normalized the 'error' event as we have many a use for a good 'ol lower cased error event
		// Its return will not be the same as pluploads - it will return an error message and an assetId (if it can get itoff the details)
		let eventPrefix;
		// Clean up any old listeners (really only an issue with Netx.asset.current)
		if (oldAssetId) {
			eventPrefix = 'upload:asset:customThumbnail:' + oldAssetId;
			// asset:customThumbnail:upload:<assetId>:error
			// asset:customThumbnail:upload:<assetId>:FilesAdded
			// asset:customThumbnail:upload:<assetId>:UploadProgress
			// asset:customThumbnail:upload:<assetId>:polling
			// asset:customThumbnail:upload:<assetId>:success
			this.stopListening(dispatcher, eventPrefix + ':error');
			this.stopListening(dispatcher, eventPrefix + ':FilesAdded');
			this.stopListening(dispatcher, eventPrefix + ':UploadProgress');
			this.stopListening(dispatcher, eventPrefix + ':progress');
			this.stopListening(dispatcher, eventPrefix + ':success');
		}

		if (!this.id) return;

		// Events for custom thumbnail
		eventPrefix = 'upload:asset:customThumbnail:' + this.id;

		this
			// Listen for error
			// asset:customThumbnail:upload:<assetId>:error
			.listenTo(dispatcher, eventPrefix + ':error', (error) => {
				this.set({
					progress: 0,
					updating: false,
				});
			})
			// Listen for FilesAdded
			// This basically means that a new custom thumbnail is being uploaded
			// asset:customThumbnail:upload:<assetId>:FilesAdded
			.listenTo(dispatcher, eventPrefix + ':FilesAdded', (pluploader, file) => {
				this.progress.update(0, i18n.t('i18n.addingCustomThumbnail'));
			})
			// Listen for polling
			// Thumbnail has been uploaded and the job is running on the server
			// Half the progress and add 50 (two part process and we are the second part)
			// asset:customThumbnail:upload:<assetId>:polling
			.listenTo(dispatcher, eventPrefix + ':progress', (batchJob, progress) => {
				this.progress.update(
					parseInt(progress),
					i18n.t('i18n.settingCustomThumbnail'),
				);
			})
			// Listen for suceess
			// Thumbnail uploaded and job complete
			// asset:customThumbnail:upload:<assetId>:success
			.listenTo(dispatcher, eventPrefix + ':succeeded', (file) => {
				this.progress.complete();
				// If we did not have a thumbUrl at all, just ignore as we are some sort of dummy asset model
				if (!this.get('thumbUrl')) return;

				// Delay slightly since this could have gone way too fast (avoid janky progress)
				_.delay(() => {
					this.fetch().then(() => {
						// TODO: Need to discuss the DAM updating the moddate when a custom thumbnail is selected
						this.trigger('thumbnailUpdated', file);
					});
				}, 1000);
			});
	}

	/**
	 * Deduces the url required to access asset media (video, audio, zoom)
	 * You know, so we are not repeating this logic all over the place in views etc
	 * This makes the change silently as nothing abotu the asset has actually changed or needed to come from the server
	 * These attributes are defined in clientAttrs and cleaned up when this.self is called
	 * @method
	 */
	setMediaUrls(attrs) {
		if (!this.id) return;

		// Check and see if we are video, audio etc and set proper urls
		this.set('audioUrl', this.hasAudio(attrs) || '');
		this.set('videoUrl', this.hasVideo(attrs) || '');
		this.set('zoomUrl', this.hasZoom(attrs) || '');
	}

	/**
	 * Pretend this is a thing
	 * @method
	 */
	setChecksum() {
		if (checksumEnabled()) {
			this.set('checksum', this.get('attribute4'));
		}
	}

	/**
	 * @method
	 */
	unexpire() {
		return this.set('expirationDate', 0).expireAsset();
	}

	/**
	 * @deprecated
	 * setCombo() and getCombo() interface with the DAM's internal representation of a choice list, aka a Pulldown,
	 * as used for attributes of type 'select', 'multiselect' and 'tags'.
	 *
	 * setCombo: Encode a combo attribute as CSV before setting.  A subsequent save() will write changes to the DAM.
	 * @method
	 * @param {String} name Name of the attribute to set choices for.
	 * @param {Array} vals An array of strings: new values for the attribute. This can be empty, to clear the list.
	 */
	setCombo(name, vals) {
		this.setAttribute(name, _.arrayToCsv(vals));
	}

	/**
	 * @deprecated
	 * Decode a combo attribute from CSV before getting
	 * @method
	 * @param {String} name Name of the attribute to get choices for.
	 * @return {Array} a list of strings.
	 */
	getCombo(name) {
		return _.csvToArray(this.getAttribute(null, name));
	}

	/**
	 * moveCategory: Move an asset from one category to another, triggering events 'asset:category:removed', 'asset:category:added' and 'asset:category:moved'.
	 * This method calls setAssetCategories(), so other categories may be removed in the process.
	 * @param {number} from ID of category to leave (if not provided, will remove from all other categories)
	 * @param {number} to ID of category to enter
	 *
	 * @todo this is too sloppy.  We need these events in order to do proper UI feedback for recategorization.
	 *       Both 'from' and 'to' should be arrays ... but really 'from' should go away & we should look at asset.categories instead.
	 *       We should then trigger asset:category:removed for every one of those that's not in 'to' .
	 */
	moveCategory(from, to) {
		if (from == to) {
			// no-op!
			return new $.Deferred().resolve();
		}

		if (from == null || isNaN(from)) from = this.get('categories');
		if (!Array.isArray(from)) from = [from];
		this.linkCategories = _.without
			.apply(_, [this.get('categories')].concat(from))
			.concat([to]);

		return this.setCategories().then(() => {
			return this.fetch().then(() => {
				// Success
				from.forEach((catId) => {
					dispatcher.trigger('asset:category:removed', this.id, catId, this);
					dispatcher.trigger('asset:category:moved', this.id, catId, to, this);
				});
				dispatcher.trigger('asset:category:added', this.id, to, this);

				// DAM-21440: What tasks are there to track on a single asset move?
				// this _defer is a hack around a DAM bug ...
				// but which one?
				// _.defer(() => {
				// 	dispatcher.trigger('asset:tasks:track', this.id);
				// });
			});
		});
	}

	/**
	 * @method
	 * @return {boolean} Is document
	 */
	isDocument() {
		return (
			(this.get('constituentIds').length > 0 && this.isPDF()) ||
			this.get('fileFormatFamily').includes('document')
		);
	}
	/**
	 * Check if we are an image
	 * @method
	 * @return {boolean}
	 */
	isImage() {
		const fileFormatFamily = this.get('fileFormatFamily');
		return (
			fileFormatFamily.includes('image') ||
			fileFormatFamily.includes('raw') ||
			fileFormatFamily.includes('cameraRaw')
		);
	}

	/**
	 * @method
	 * @return {boolean} Is locked
	 */
	isLocked() {
		return this.get('readOnly') === 1;
	}

	/**
	 * Check if we are a PDF
	 * @method
	 * @return {boolean}
	 */
	isPDF() {
		return (
			_.indexOf(this.get('viewNames'), 'previewPDF') !== -1 ||
			this.get('filetypelabel') === 'pdf'
		);
	}

	/**
	 * Lock an asset (previously set asset to "read-only")
	 * @method
	 */
	lock(opts) {
		// Why we need the bean returned on updates
		// Fetching to get the appropriate permission matrix
		return this.save({readOnly: 1}, {wait: true, ...opts}).then(() => {
			return this.fetch(opts);
		});
	}

	/**
	 * Return an assetSet object defining the set that contains just this asset.
	 * @method
	 * @return {module:asset/models/assetSet} AssetSet model
	 */
	toAssetSet() {
		return new AssetSetModel({
			type: assetSetTypes.asset,
			ids: [this.id],
			models: [this],
			label: this.get('name'),
		});
	}

	/**
	 * Unlock an asset (previously unset asset from "read-only")
	 * @method
	 */
	unlock(opts) {
		// Why we need the bean returned on updates
		// Fetching to get the appropriate permission matrix
		return this.save({readOnly: 0}, {wait: true, ...opts}).then(() => {
			return this.fetch(opts);
		});
	}

	/**
	 * return a cache-busting string for the asset preview; this string should change every time the asset preview changes.
	 * @method
	 * @return string
	 */
	cacheBuster(prefix) {
		// If this model doesn't have moddate set, use the current time.
		const timeStr = this.get('moddate')
			? _.dateFromAsset(this.get('moddate')).getTime()
			: new Date().getTime();

		// A new checkin will change the preview image without modifying the moddate.
		// Therefore, encode both versionIds and moddate in the buster-string
		return (
			'?' +
			(prefix ? prefix + '&' : '') +
			't=' +
			timeStr +
			'&v=' +
			this.get('versionIds')
		);
	}

	/**
	 * @deprecated
	 * Force a local update of the asset's moddate property, so that this.cacheBuster() will return a new string,
	 * and so that any change listeners for moddate will be jingled.
	 * Moddate is a read-only property, so any future fetch will reset it to the DAM's value.
	 * But this method is useful in situations where the client already knows a change has happened,
	 * and doesn't want to re-fetch.
	 *
	 * @method
	 * @param {Date} date A date object specifying the time to stamp (if undefined, we use the current moment)
	 */
	bumpModdate(date) {
		date = date || new Date();
		this.set({moddate: _.dateToAsset(date)});
	}

	/**
	 * Check our permissionMatrix for whether a particular named bit is set.
	 * @param {string} perm One of the permissions in {module:permissions/properties/assetPermissions#matrixNames}
	 * @returns {mixed} returns true or false, or undefined (which is falsy) if the argument is undefined.
	 * @method
	 */
	// DEVNOTE: module:asset/models/asset & Netx.Models.Category use the same method.  Change it here, change it there.
	// Until we have a better include/export scheme we won't know whether asset or category module inits first,
	// so we can't define one of these in terms of the other.
	//
	//allows: module:asset/models/asset.prototype.allows
	allows(perm) {
		if (perm == null) {
			return undefined;
		}

		const matrixIndex = matrixNames.indexOf(perm);

		if (matrixIndex < 0) {
			throw new Error("Unknown permission '" + perm + "'");
		}

		// We need the DAM to return appropriate permissions for readOnly assets (even for admins)
		if (this.get('readOnly') === 1) {
			switch (perm) {
				case 'delete':
				case 'modify':
				case 'all':
					return false;
			}
		}

		// The actual test of the actual value in the actual matrix (cast to bool):
		return this.get('permissionMatrix')[matrixIndex] ? true : false;
	}

	/**
	 * Methods for MediaRich zoom feature (if enabled):
	 *
	 * This non-standard API call is proxied from the DAM to a connected MediaRich server,
	 * and tells the MR server to cache the asset from the DAM.
	 *
	 * @method
	 * @returns {promise} resolves/rejects when complete.
	 */
	cacheInMediaRich() {
		return new Promise((resolve, reject) => {
			const mediarichCacheUrl =
				// server target (proxied to MS server):
				'/mgen/NetX.ms?f=LoadAndCache&assetPath=' +
				// URL to original asset, with sessionKey added for authentication:
				encodeURIComponent(
					URL.asset +
						'/' +
						this.id +
						'/original/' +
						this.get('file') +
						'?sessionKey=' +
						session.get('sessionKey'),
				) +
				// Session key again, because sessions are key:
				'&sessionKey=' +
				session.get('sessionKey');

			$.ajax(mediarichCacheUrl).then((responseJSON, responseText, jqXHR) => {
				// response must be 200 (ok)
				if (jqXHR && jqXHR.status != 200) {
					return reject(`Bad response from MediaRich server: ${responseText}`);
				} else if (
					typeof responseJSON === 'string' &&
					responseJSON.includes('not found on NetX')
				) {
					return reject(responseJSON);
				}
				resolve(responseJSON);
			}, reject);
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'Asset';
	}
}
